import { useCallback, useMemo, useRef, useState } from 'react';
import { Address, AddressCategory, AddressCountry, AddressState } from '@/@types/client-api';
import { SelectorItem } from '@/components/ui/Filter/types';
import { usePostAddressMutation, useUpdateAddressMutation } from '@/redux/api/client/userAddresses';
import useToast from './useToast';
import { isInvalidZipFormat } from '@/utils/validations';

const useAddressForm = (
  addressCategoryLookup: AddressCategory[],
  addressStateLookup: AddressState[],
  addressCountryLookup: AddressCountry[],
  refetchCheckout?: () => void,
  refetchAddresses?: () => void,
  setIsNewAddressAdded?: (value: boolean) => void,
  onOpenChange?: (value: boolean) => void,
  onDelete?: (address: Address) => void,
  data?: Address
) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [postAddress] = usePostAddressMutation();
  const [updateAddress] = useUpdateAddressMutation();
  const { showSuccessMessage, showFailMessage } = useToast();
  const [addressCountry, setAddressCountry] = useState<string>('');
  const isUnitedStatesOrCanada = ['USA', 'CAN'].includes(addressCountry.toUpperCase());
  const isUnitedStates = addressCountry.toUpperCase() === 'USA';

  const [addressCategory, setAddressCategory] = useState<string>('');
  const [attention, setAttention] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [address1, setAddress1] = useState<string>('');
  const [address2, setAddress2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [addressState, setAddressState] = useState<string>('');
  const [validationFailed, setValidationFailed] = useState(false);
  const [zip, setZip] = useState<string>('');
  const [billingAddress, setBillingAddress] = useState<boolean>(false);
  const [shippingAddress, setShippingAddress] = useState<boolean>(false);
  const typeRef = useRef<HTMLButtonElement>(null);
  const stateRef = useRef<HTMLButtonElement>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);

  const addressCategories = useMemo(
    () =>
      addressCategoryLookup.map(
        (item): SelectorItem => ({
          value: item.sk?.toString() ?? '',
          label: item.shortDescription ?? ''
        })
      ),
    [addressCategoryLookup]
  );

  const addressStates = useMemo(() => {
    const countryStates = addressCountry
      ? addressStateLookup.filter(item => item.countryId === addressCountry)
      : addressStateLookup;
    const filteredStates = countryStates.filter(item => item.pk !== '0');
    return filteredStates.map(
      (item): SelectorItem => ({
        value: item.pk ?? '',
        label: item.stateName ?? ''
      })
    );
  }, [addressCountry, addressStateLookup]);

  const addressCountries = useMemo(
    () =>
      addressCountryLookup.map(
        (item): SelectorItem => ({
          value: item.pk ?? '',
          label: item.countryName ?? ''
        })
      ),
    [addressCountryLookup]
  );

  const saveAddress = useCallback(
    async (data: Address) => {
      const isAddingNewAddress = data === undefined;
      setIsProcessing(true);
      try {
        if (isAddingNewAddress) {
          await postAddress(data).unwrap();
        } else {
          await updateAddress(data).unwrap();
        }
        await refetchAddresses?.();
        await refetchCheckout?.();
        setIsNewAddressAdded?.(true);
        showSuccessMessage(`Address ${isAddingNewAddress ? 'added' : 'updated'}`);
      } catch (error) {
        showFailMessage('Error saving address');
      } finally {
        setIsProcessing(false);
      }
    },
    [
      postAddress,
      refetchAddresses,
      refetchCheckout,
      setIsNewAddressAdded,
      showFailMessage,
      showSuccessMessage,
      updateAddress
    ]
  );

  const handleOnClose = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleOnDelete = useCallback(() => {
    if (data) {
      handleOnClose();
      onDelete?.(data);
    }
  }, [data, handleOnClose, onDelete]);

  const handleValidateFields = useCallback((): boolean => {
    const isValid =
      !!addressCategory &&
      !!address1 &&
      !!city &&
      (!isUnitedStatesOrCanada || !!addressState) &&
      (!isUnitedStates || !isInvalidZipFormat(zip)) &&
      !!addressCountry;

    if (!isValid) {
      const options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' };
      if (!addressCategory) {
        typeRef.current?.scrollIntoView(options);
        typeRef.current?.focus();
      } else if (!address1) {
        addressRef.current?.scrollIntoView(options);
        addressRef.current?.focus();
      } else if (!city) {
        cityRef.current?.scrollIntoView(options);
        cityRef.current?.focus();
      } else if (isUnitedStatesOrCanada && !addressState) {
        stateRef.current?.scrollIntoView(options);
        stateRef.current?.focus();
      } else if (isUnitedStatesOrCanada && !zip) {
        zipRef.current?.scrollIntoView(options);
        zipRef.current?.focus();
      }
    }

    return isValid;
  }, [
    address1,
    addressCategory,
    addressCountry,
    addressState,
    city,
    isUnitedStates,
    isUnitedStatesOrCanada,
    zip
  ]);

  const onSave = useCallback(() => {
    handleOnClose();
    saveAddress({
      sk: data?.sk,
      addressCategory_SK: parseInt(addressCategory),
      attention,
      company,
      address1,
      address2,
      city,
      state: isUnitedStatesOrCanada ? addressState : undefined,
      zip: isUnitedStates ? zip.split('-')[0] : zip,
      plus4: isUnitedStates ? zip.split('-')[1] : undefined,
      country: addressCountry,
      isPrimaryBAddress: billingAddress,
      isPrimarySAddress: shippingAddress
    });
  }, [
    address1,
    address2,
    addressCategory,
    addressCountry,
    addressState,
    attention,
    billingAddress,
    city,
    company,
    data?.sk,
    handleOnClose,
    isUnitedStates,
    isUnitedStatesOrCanada,
    saveAddress,
    shippingAddress,
    zip
  ]);

  const handleOnSave = useCallback(() => {
    const fieldsValid = handleValidateFields();
    if (fieldsValid) {
      onSave();
    } else {
      setValidationFailed(true);
    }
  }, [handleValidateFields, onSave]);

  return {
    addressCategories,
    addressStates,
    addressCountries,
    addressCountry,
    validationFailed,
    addressState,
    attention,
    addressCategory,
    company,
    address1,
    address2,
    city,
    billingAddress,
    shippingAddress,
    zip,
    zipRef,
    typeRef,
    stateRef,
    cityRef,
    addressRef,
    isUnitedStatesOrCanada,
    isUnitedStates,
    isProcessing,
    setAddressCountry,
    handleValidateFields,
    handleOnSave,
    onSave,
    handleOnClose,
    handleOnDelete,
    setBillingAddress,
    setShippingAddress,
    setAddressCategory,
    setAttention,
    setCompany,
    setAddress1,
    setAddress2,
    setCity,
    setAddressState,
    setZip
  };
};

export default useAddressForm;
